import React, { useEffect, useRef, useState, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { I18n } from 'react-redux-i18n'
import download from 'downloadjs'

import { FILE_MAX_SIZE_CARTERL_QR } from 'constants/filesMaxSize'
import { resource } from 'api'
import ModalWrapper from 'components/ChannelView/ModalWrapper'
import UploadFiles from 'components/UploadFiles'
import { Button } from 'components/Form'
import { ButtonTag } from 'components/Form'
import { getStaticImage } from 'utils/statics'
import PreviewAppResource from './FileUploader/PreviewAppResource/index'
import { toPng } from '../../../../utils/htmlToImage/index'

const FileUploaderLogo = (props) => {
  const {
    prop, label, channel, description, onChange, acceptTypes, value, size,
    dimensions, restore, defaultValue, required, requireLogo, primaryColor
  } = props

  const [reset, setReset] = useState(restore)
  const _acceptTypes = acceptTypes ? acceptTypes : 'image/*,video/*'
  const [attachment, setAttachment] = useState([])
  const company = useSelector((store) => store.companies)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isModalPreviewOpen, setIsModalPreviewOpen] = useState(false)
  const [uploading, setUploading] = useState(false)
  const resourcePreviewRef = useRef(null)
  const [downloading, setDownloading] = useState(false)

  useEffect(() => {
    if (value === '') {
      setAttachment([{ url: defaultValue }])
      onChange(defaultValue)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (value !== defaultValue) {
      setReset(true)
    } else {
      setReset(false)
    }
    if (value.length) {
      setAttachment([{ url: value }])
    } else {
      setAttachment([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  const clearAttachment = () => {
    setAttachment([])
    onChange('')
  }

  const onToggleModal = () => setIsModalOpen(!isModalOpen)

  const handleOpenUploader = () => onToggleModal()

  const onUploadFiles = async (files) => {
    setUploading(true)

    try {
      if (files.length > 0) {
        const formData = new FormData()
        const dataBody = {
          companyType: company.type,
          companyToken: company.hash,
          typeUpload: 'userFile',
        }

        formData.append('data', JSON.stringify(dataBody))
        formData.append('files', files[0].file)

        const { data } = await resource.uploadFiles(formData)

        setAttachment([
          { url: data.attachments[0].urlLong }
        ])
        onChange(data.attachments[0].urlLong)
      }
    } catch (error) {
      console.error(error)
    }

    setUploading(false)
  }

  const toggleModalPreview = () => setIsModalPreviewOpen(!isModalPreviewOpen)

  const handlerDownload = useCallback(async (url) => {
    if (!resourcePreviewRef && !resourcePreviewRef.current) return
    setDownloading(true)

    try {
      if (_acceptTypes.includes('image')) {
        const dataUrl = await toPng(resourcePreviewRef.current, { pixelRatio: 1 })
        download(dataUrl, `${I18n.t(label)}.png`)
      }

      if (_acceptTypes.includes('video')) {
        download(url)
      }
    } catch (error) {
      console.error(error)
    }

    setDownloading(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resourcePreviewRef])

  return (
    <div className='channel-field file'>
      <div className='file__header'>
        {label && <label className='text-editor-title'>{I18n.t(label)} <span className='required'>{required ? '*' : ''}</span></label>}
        {reset && !_acceptTypes.includes('video') && !prop.includes('logo') && (channel.key === 'android' || channel.key === 'ios') ? <ButtonTag value='Restablecer' onClick={() => onChange(defaultValue)} /> : null}
      </div>
      <div className='field-content grid grid--small'>
        {attachment.length ? (
          <PreviewAppResource
            url={attachment[0].url}
            targetOrientation='width'  // width / height
            targetSize={75}
            acceptTypes={_acceptTypes}
            resourcePreviewRef={resourcePreviewRef}
            reset={reset}
            prop={prop}
            primaryColor={primaryColor}
            requireLogo={requireLogo}
            companyLogo={channel.app && channel.app.logo}
            defaultCompanyLogo={getStaticImage("/qr-poster-default_logo-v2.png")}
            removeResource={clearAttachment}
            previewResource={toggleModalPreview}
          />
        ) : (
          <button className='uploader-button' onClick={handleOpenUploader}>
            <img className='uploader-button__icon' src={`${getStaticImage('/apps/log-out.svg')}`} alt='Upload' />
            {I18n.t('dashboard_upload-file')}
          </button>
        )}
        {description && <p className='description' dangerouslySetInnerHTML={{ __html: I18n.t(description) }} />}
      </div>
      <ModalWrapper
        id='modalChannelUploadFiles'
        header={<h1>{I18n.t('dashboard_upload-files')}</h1>}
        body={
          <UploadFiles
            uploading={uploading}
            uploadFiles={onUploadFiles}
            multiple={false}
            acceptTypes={_acceptTypes}
            cancel={onToggleModal}
            filesUploaded={attachment}
            onRemoveUploadedFile={() => { onUploadFiles([]); onChange('') }}
            dimensions={dimensions}
            messageErrorDimensions={I18n.t('error_profile_file_incorrect_dimensions')}
            labelSize={description ? I18n.t(description).split('Puedes')[0] : I18n.t('dashboard_drop-files-max-size-2mb')}
            maxSize={size ? size : FILE_MAX_SIZE_CARTERL_QR}
            messageErrorSize={I18n.t('error_profile_file_too_big')}
          />
        }
        isOpen={isModalOpen}
        toggle={onToggleModal}
        showClose={true}
        backdrop={'static'}
      />
      <ModalWrapper
        id='modalResourcePreview'
        isOpen={isModalPreviewOpen}
        toggle={toggleModalPreview}
        header={<h1>Vista previa: {I18n.t(label)}</h1>}
        body={attachment.length && (
          <PreviewAppResource
            url={attachment[0].url}
            targetOrientation='width'
            targetSize={450}
            acceptTypes={_acceptTypes}
            resourcePreviewRef={resourcePreviewRef}
            reset={reset}
            prop={prop}
            primaryColor={primaryColor}
            requireLogo={requireLogo}
            companyLogo={channel.app && channel.app.logo}
            defaultCompanyLogo={getStaticImage("/qr-poster-default_logo-v2.png")}
            videoControls
          />
        )}
        footer={
          <div className='preview-modal-buttons'>
            <Button secondary value={I18n.t('dashboard_download')} onClick={() => handlerDownload(attachment[0].url)} isSubmitting={downloading} />
            <Button value={I18n.t('dashboard_close')} onClick={toggleModalPreview} />
          </div>
        }
      />
    </div>
  )
}

export default FileUploaderLogo
